import { noop } from 'lodash-es';
import { createBrowserRouter, Navigate, redirect } from 'react-router-dom';
import { BoostIconDisplay } from 'src/components/SponsoredListings';
import { Content } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { Activity } from 'src/navigations/Routes/Activity';
import { Email, EmailNavPageNameHandle } from 'src/navigations/Routes/Email';
import { HelpCenter } from 'src/navigations/Routes/HelpCenter';
import { Home } from 'src/navigations/Routes/Home/Home';
import { Inventory } from 'src/navigations/Routes/Inventory';
import { InventoryEvent } from 'src/navigations/Routes/InventoryEvent';
import { InventoryEventPageRedirect } from 'src/navigations/Routes/InventoryEvent/InventoryEventPageRedirect';
import { InventorySearch } from 'src/navigations/Routes/InventorySearch';
import { InventorySearchV2 } from 'src/navigations/Routes/InventorySearchV2';
import {
  Login,
  LoginCallback,
  LoginFailure,
  Logout,
} from 'src/navigations/Routes/Login';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { Notifications } from 'src/navigations/Routes/Notifications';
import { OnSaleDiscovery } from 'src/navigations/Routes/OnSaleDiscovery';
import { OnSaleEvent } from 'src/navigations/Routes/OnSaleEvent/OnSaleEvent';
import { Payments } from 'src/navigations/Routes/Payments';
import { PurchaseEvent } from 'src/navigations/Routes/PurchaseEvent/PurchaseEvent';
import { Purchases } from 'src/navigations/Routes/Purchases';
import { PurchaseSearch } from 'src/navigations/Routes/PurchaseSearch/PurchaseSearch';
import { PurchaseSearchV2 } from 'src/navigations/Routes/PurchaseSearchV2';
import { ReachProSupport } from 'src/navigations/Routes/ReachProSupport';
import { RedirectToStubHubUk } from 'src/navigations/Routes/RedirectToStubHubUk';
import { ReleaseNotes } from 'src/navigations/Routes/ReleaseNotes';
import { Reports, ReportsV2 } from 'src/navigations/Routes/Reports';
import { ReportsCommission } from 'src/navigations/Routes/ReportsCommission';
import { ReportsCostOfGoodsSold } from 'src/navigations/Routes/ReportsCostOfGoodsSold';
import { ReportsInventory } from 'src/navigations/Routes/ReportsInventory';
import { ReportsInventoryV2 } from 'src/navigations/Routes/ReportsInventory/ReportsInventoryV2';
import { ReportsPurchase } from 'src/navigations/Routes/ReportsPurchase';
import { ReportsPurchaseOrderLineItem } from 'src/navigations/Routes/ReportsPurchaseOrderLineItem';
import { ReportsSale } from 'src/navigations/Routes/ReportsSale';
import { ReportsSaleV2 } from 'src/navigations/Routes/ReportsSale/ReportsSaleV2';
import { ReportsSaleCostAdjustments } from 'src/navigations/Routes/ReportsSaleCostAdjustments';
import { ReportsSaleLineItem } from 'src/navigations/Routes/ReportsSaleLineItem';
import { ReportsSoldTicket } from 'src/navigations/Routes/ReportsSoldTicket';
import { ReportsTicketGroup } from 'src/navigations/Routes/ReportsTicketGroup';
import { Sales } from 'src/navigations/Routes/Sales';
import { SaleSearch } from 'src/navigations/Routes/SaleSearch';
import { SaleSearchV2 } from 'src/navigations/Routes/SaleSearchV2';
import { SalesEvent } from 'src/navigations/Routes/SalesEvent/SalesEvent';
import { Settings, SettingsViews } from 'src/navigations/Routes/Settings';
import { SettingTab } from 'src/navigations/Routes/Settings/constants';
import { SyncCenter } from 'src/navigations/Routes/Settings/Views/SyncCenter';
import { SponsoredListings } from 'src/navigations/Routes/SponsoredListings';
import { TicketUtilsLanding } from 'src/navigations/Routes/TicketUtilsLanding';
import { SaleAlertIcon } from 'src/svgs';
import { PaymentsIcon } from 'src/svgs/PaymentsIcon';
import { ReportsIcon } from 'src/svgs/ReportsIcon';
import {
  ChatIcon,
  HomeOutlineIcon,
  IconsProps,
  PaperTicketsOutlineIcon,
  PlusIcon,
  SearchSolidIcon,
  SellerTraderOutlineIcon,
  SellOutlineIcon,
  SettingsIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { DeviceAwareContentWrapper } from 'src/utils/contentUtils';
import { RootErrorPage } from 'src/views';
import { CommandOptionType } from 'src/WebApiController';
import { UserSetting } from 'src/WebApiController';

import App from './App';

export type AppRouteConfigType = {
  favorite?: UserSetting;
  view: MainRoute;
  path: string;
  label: React.ReactNode;
  icon: (props: IconsProps) => JSX.Element;
  subRoutes?: Record<
    string,
    {
      Path: string;
      Icon: (props: IconsProps) => JSX.Element;
    }
  >;
  commandOptionType?: CommandOptionType;
};

export const AppRouteConfigs: { [key: string]: AppRouteConfigType } = {
  [MainRoute.Home]: {
    view: MainRoute.Home,
    path: '/home',
    label: <Content id={ContentId.Home} />,
    icon: HomeOutlineIcon,
    commandOptionType: CommandOptionType.NavigateHome,
  },
  [MainRoute.Inventory]: {
    view: MainRoute.Inventory,
    path: '/inventory',
    label: <Content id={ContentId.Inventory} />,
    icon: PaperTicketsOutlineIcon,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
    },
    favorite: UserSetting.InventoryMenuTabFavorite,
    commandOptionType: CommandOptionType.NavigateInventory,
  },
  [MainRoute.Sales]: {
    view: MainRoute.Sales,
    path: '/sales',
    label: (
      <DeviceAwareContentWrapper
        defaultId={ContentId.Sales}
        mobileId={ContentId.Sales}
      />
    ),
    icon: SaleAlertIcon,
    commandOptionType: CommandOptionType.NavigateSales,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
    },
    favorite: UserSetting.SaleMenuTabFavorite,
  },
  [MainRoute.Purchases]: {
    view: MainRoute.Purchases,
    path: '/purchases',
    label: <Content id={ContentId.Purchases} />,
    icon: SellOutlineIcon,
    commandOptionType: CommandOptionType.NavigatePurchases,
    subRoutes: {
      Search: { Path: '/search', Icon: SearchSolidIcon },
      SearchV2: { Path: '/search-v2', Icon: SearchSolidIcon },
      Create: { Path: '?uniquePurchaseId=0', Icon: PlusIcon },
    },
    favorite: UserSetting.PurchaseMenuTabFavorite,
  },
  [MainRoute.Payments]: {
    view: MainRoute.Payments,
    path: '/payments',
    label: <Content id={ContentId.Payments} />,
    icon: PaymentsIcon,
    commandOptionType: CommandOptionType.NavigatePayments,
  },
  [MainRoute.Reports]: {
    view: MainRoute.Reports,
    path: '/reports',
    label: <Content id={ContentId.Reports} />,
    icon: ReportsIcon,
    commandOptionType: CommandOptionType.NavigateReports,
  },
  [MainRoute.ReportsV2]: {
    view: MainRoute.ReportsV2,
    path: '/reports/v2',
    label: 'Reports V2', // no need to translate - once we release - we will need to use ContentId.Report again
    icon: ReportsIcon,
  },
  [MainRoute.SponsoredListings]: {
    view: MainRoute.SponsoredListings,
    path: '/sponsoredlistings',
    label: <Content id={ContentId.SponsoredListings} />,
    icon: BoostIconDisplay,
  },
  [MainRoute.Discovery]: {
    view: MainRoute.Discovery,
    path: '/discovery',
    label: <Content id={ContentId.EventDiscovery} />,
    icon: SearchSolidIcon,
  },
};

export const MenuItems: { [key: string]: AppRouteConfigType } = {
  [MainRoute.Email]: {
    view: MainRoute.Email,
    path: '/messages',
    label: <Content id={ContentId.Messages} />,
    icon: ChatIcon,
  },
  [MainRoute.ReachProSupport]: {
    view: MainRoute.ReachProSupport,
    path: '/reachpro-support',
    label: <Content id={ContentId.SupportCenter} />,
    icon: SellerTraderOutlineIcon,
  },
  [MainRoute.Settings]: {
    view: MainRoute.Settings,
    path: '/settings',
    label: <Content id={ContentId.Settings} />,
    icon: SettingsIcon,
    commandOptionType: CommandOptionType.NavigateSettings,
  },
} as const;

export const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: (
      <div>
        <RootErrorPage error={undefined} resetErrorBoundary={noop} />
      </div>
    ),
    children: [
      {
        path: '/',
        loader() {
          return redirect('/home');
        },
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/login/callback',
        element: <LoginCallback />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
      {
        path: '/unauthorized',
        element: <LoginFailure />,
      },
      {
        path: AppRouteConfigs[MainRoute.Home].path,
        element: <Home />,
      },
      {
        path: AppRouteConfigs[MainRoute.Inventory].path,
        element: <Inventory />,
      },
      {
        path: '/inventory/event',
        element: <InventoryEvent />,
      },
      {
        path: '/inventory/event/:eventId',
        element: <InventoryEventPageRedirect />,
      },
      {
        path: '/inventory/search',
        element: <InventorySearch />,
      },
      {
        path: '/inventory/search-v2',
        element: <InventorySearchV2 />,
      },
      {
        path: AppRouteConfigs[MainRoute.Discovery].path,
        element: <OnSaleDiscovery />,
      },
      {
        path: '/discovery/event',
        element: <OnSaleEvent />,
      },
      {
        path: AppRouteConfigs[MainRoute.Discovery].path,
        element: <OnSaleDiscovery />,
      },
      {
        path: AppRouteConfigs[MainRoute.Sales].path,
        element: <Sales />,
      },
      {
        path: '/sales/event',
        element: <SalesEvent />,
      },
      {
        path: '/sales/search',
        element: <SaleSearch />,
      },
      {
        path: '/sales/search-v2',
        element: <SaleSearchV2 />,
      },
      {
        path: AppRouteConfigs[MainRoute.Purchases].path,
        element: <Purchases />,
      },
      {
        path: AppRouteConfigs[MainRoute.Payments].path,
        element: <Payments />,
      },
      {
        path: '/purchases/event',
        element: <PurchaseEvent />,
      },
      {
        path: '/purchases/search',
        element: <PurchaseSearch />,
      },
      {
        path: '/purchases/search-v2',
        element: <PurchaseSearchV2 />,
      },
      {
        path: '/redirect-uk',
        element: <RedirectToStubHubUk />,
      },
      {
        path: '/ticketutils',
        element: <TicketUtilsLanding />,
      },
      {
        path: '/notifications',
        element: <Notifications />,
      },
      {
        path: '/release-notes',
        element: <ReleaseNotes />,
      },
      {
        path: '/help',
        element: <HelpCenter />,
      },
      {
        path: '/reports/v2',
        element: <ReportsV2 />,
      },
      {
        path: '/reports',
        element: <Reports />,
      },
      {
        path: '/reports/inventory/:reportId',
        element: <ReportsInventory />,
      },
      {
        path: '/reports/sale/:reportId',
        element: <ReportsSale />,
      },
      {
        path: '/reports/inventory/v2/:reportId',
        element: <ReportsInventoryV2 />,
      },
      {
        path: '/reports/commission/v2/:reportId',
        element: <ReportsCommission />,
      },
      {
        path: '/reports/purchase/v2/:reportId',
        element: <ReportsPurchase />,
      },
      {
        path: '/reports/salelineitem/v2/:reportId',
        element: <ReportsSaleLineItem />,
      },
      {
        path: '/reports/soldticket/v2/:reportId',
        element: <ReportsSoldTicket />,
      },
      {
        path: '/reports/sale/v2/:reportId',
        element: <ReportsSaleV2 />,
      },
      {
        path: '/reports/purchaseorderlineitem/v2/:reportId',
        element: <ReportsPurchaseOrderLineItem />,
      },
      {
        path: '/reports/costofgoodssold/v2/:reportId',
        element: <ReportsCostOfGoodsSold />,
      },
      {
        path: '/reports/salecostadjustments/v2/:reportId',
        element: <ReportsSaleCostAdjustments />,
      },
      {
        path: '/reports/ticketgroup/v2/:reportId',
        element: <ReportsTicketGroup />,
      },
      {
        path: '/activity',
        element: <Activity />,
      },
      {
        path: '/sync-center',
        element: <SyncCenter />,
      },
      {
        path: AppRouteConfigs[MainRoute.SponsoredListings].path,
        element: <SponsoredListings />,
      },
      /**
       * Settings Routes
       */
      {
        path: '/settings',
        element: <Settings />,
        children: [
          {
            index: true,
            element: (
              <Navigate to={`/settings/${SettingTab.MyAccount}`} replace />
            ),
          },
          {
            path: SettingTab.MyAccount,
            element: <SettingsViews.MyAccount />,
          },
          {
            path: SettingTab.CurrencyConversion,
            element: <SettingsViews.CurrencyConversion />,
          },
          {
            path: SettingTab.FulfillmentProviders,
            element: <SettingsViews.FulfillmentProviders />,
          },
          {
            path: SettingTab.PurchaseOrder,
            element: <SettingsViews.PurchaseOrderSetting />,
          },
          {
            path: SettingTab.Inventory,
            element: <SettingsViews.InventorySetting />,
          },
          {
            path: SettingTab.Accounting,
            element: <SettingsViews.AccountingSetting />,
          },
          {
            path: SettingTab.UserManagement,
            element: <SettingsViews.UsersManagement />,
            children: [
              {
                path: '',
                element: (
                  <Stack direction="column" gap="l">
                    <SettingsViews.Users />
                    <SettingsViews.Roles />
                    <SettingsViews.Teams />
                  </Stack>
                ),
              },
              {
                path: SettingTab.UserManagement_Users,
                element: <SettingsViews.Users />,
              },
              {
                path: SettingTab.UserManagement_Roles,
                element: <SettingsViews.Roles />,
              },
              {
                path: SettingTab.UserManagement_Teams,
                element: <SettingsViews.Teams />,
              },
            ],
          },
          {
            path: SettingTab.VendorManagement,
            element: <SettingsViews.VendorManagement />,
            children: [
              {
                path: '',
                element: (
                  <Stack direction="column" gap="l">
                    <SettingsViews.Payments />
                    <SettingsViews.Vendors />
                  </Stack>
                ),
              },
              {
                path: SettingTab.VendorManagement_Vendors,
                element: <SettingsViews.Vendors />,
              },
              {
                path: SettingTab.VendorManagement_Payments,
                element: <SettingsViews.Payments />,
              },
            ],
          },
          {
            path: SettingTab.SyncCenter,
            element: <SettingsViews.SyncCenter />,
          },
          {
            path: SettingTab.SellerAccount,
            element: <SettingsViews.SellerAccount />,
          },
          {
            path: SettingTab.AutoPricing,
            element: <SettingsViews.AccountAutoPricingSettings />,
          },
          {
            path: SettingTab.DialogSuppression,
            element: <SettingsViews.DialogSuppression />,
          },
          {
            path: SettingTab.EventDiscovery,
            element: <SettingsViews.EventDiscovery />,
          },
        ],
      },
      {
        path: '/messages',
        element: <Email />,
        children: [
          {
            children: [
              { index: true },
              { path: 'support/:sellerSupportCaseId' },
              { path: ':inboundEmailId' },
            ],
            handle: {
              pageName: 'inbox',
            } satisfies EmailNavPageNameHandle,
          },
          {
            path: 'uncategorized',
            children: [{ index: true }, { path: ':inboundEmailId' }],
            handle: {
              pageName: 'uncategorized',
            } satisfies EmailNavPageNameHandle,
          },
          {
            path: 'recently-categorized',
            children: [{ index: true }, { path: ':inboundEmailId' }],
            handle: {
              pageName: 'recentlyCategorized',
            } satisfies EmailNavPageNameHandle,
          },
        ],
      },
      {
        path: '/reachpro-support',
        element: <ReachProSupport />,
        children: [{ index: true }, { path: ':reachProConversationId' }],
      },
    ],
  },
]);
